<template>
  <el-dialog
    :visible.sync="modalVisible"
    title="代投扣量"
    width="782px"
    :close-on-click-modal="false"
    :show-close="false"
    @open="onModalOpen"
    @close="onModalClose"
  >
    <el-form
      ref="formRef"
      :model="configForm"
      :rules="rules"
      :style="{ height: configForm.enable ? '555px' : '165px' }"
      class="proxy-control-form ghost-button-styles"
      label-width="150px"
    >
      <el-form-item label-width="80px">
        <p style="line-height: 22px; color: orange">
          说明：该数据录入用于【代投后台】-【推广数据】页面的显示，该页面在【天工开物-投放-代投账号管理】代投账号进行设置
        </p>
      </el-form-item>

      <el-form-item label="投放计划">
        {{ planInfo.planName }}
      </el-form-item>

      <el-form-item label="扣量开关">
        <el-switch v-model="configForm.enable" :active-value="1" :inactive-value="0"></el-switch>
      </el-form-item>

      <template v-if="configForm.enable">
        <el-form-item label="扣量模式">
          <el-radio-group v-model="configForm.source">
            <el-radio-button ghost label="1"> 数据录入 </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="数据时间" prop="tdate">
          <el-date-picker v-model="configForm.tdate" type="date" value-format="yyyy-MM-dd"></el-date-picker>
        </el-form-item>

        <el-form-item label="激活数" prop="activeNum">
          <el-input v-model="configForm.activeNum"></el-input>
        </el-form-item>

        <el-form-item label="注册数" prop="newUserNum">
          <el-input v-model="configForm.newUserNum"></el-input>
        </el-form-item>

        <el-form-item label="创角数" prop="createRoleNum">
          <el-input v-model="configForm.createRoleNum"></el-input>
        </el-form-item>

        <el-form-item label="总流水" prop="sumMoney">
          <el-input v-model="configForm.sumMoney"></el-input>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="onAddConfigTableRow">+ 数据录入</el-button>
        </el-form-item>

        <el-form-item label="数据详情">
          <el-table class="common-el-table-styles" :data="tableData" border row-key="date" :row-class-name="rowClassName">
            <el-table-column prop="tdate" label="数据时间" show-overflow-tooltip> </el-table-column>
            <el-table-column prop="activeNum" label="激活数" show-overflow-tooltip> </el-table-column>
            <el-table-column prop="newUserNum" label="注册数" show-overflow-tooltip> </el-table-column>
            <el-table-column prop="createRoleNum" label="创角数" show-overflow-tooltip> </el-table-column>
            <el-table-column prop="sumMoney" label="总流水" show-overflow-tooltip> </el-table-column>
            <el-table-column label="操作">
              <template slot-scope="{ $index }">
                <el-button type="text" @click="onTableRowRemove($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-form-item>
      </template>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="onModalClose">关闭</el-button>
      <el-button type="primary" :loading="submitLoading" @click="onConfigConfirm"> 确定 </el-button>
    </span>
  </el-dialog>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import moment from 'moment'
import { Message } from 'view-design'
import { queryProxyImportDataByPlanId, insertProxyImportDataByPlanId } from '@/api/track.plan.model'

// eslint-disable-next-line
const props = defineProps({
  value: {
    type: Boolean
  },
  planInfo: {
    type: Object
  }
})

// eslint-disable-next-line
const emit = defineEmits(['input'])

const submitLoading = ref(false)
const formRef = ref(null)

const positiveIntegerValidator = (rule, value, callback) => {
  const str = String(value || '').trim()
  if (!str) {
    return callback(new Error('请输入正整数'))
  }

  if (Number(str) > 999999999.9999) {
    return callback(new Error('请输入小于10亿的数字'))
  }
  callback()
}
const numPattern = {
  pattern: /^[0-9]\d*$/,
  message: '请输入正整数或0',
  trigger: 'blur'
}
const rules = {
  tdate: [{ required: true, message: '请选择数据时间', trigger: 'change' }],
  activeNum: [
    { required: true, message: '请输入激活数', trigger: 'blur' },
    numPattern,
    { validator: positiveIntegerValidator, trigger: 'blur' }
  ],
  newUserNum: [
    { required: true, message: '请输入注册数', trigger: 'blur' },
    numPattern,
    { validator: positiveIntegerValidator, trigger: 'blur' }
  ],
  createRoleNum: [
    { required: true, message: '请输入创角数', trigger: 'blur' },
    numPattern,
    { validator: positiveIntegerValidator, trigger: 'blur' }
  ],
  sumMoney: [
    { required: true, message: '请输入总流水', trigger: 'blur' },
    {
      pattern: /^[0-9]\d*(\.\d{1,2})?$/,
      message: '请输入最多2位小数的正数',
      trigger: 'blur'
    },
    {
      validator: positiveIntegerValidator,
      trigger: 'blur'
    }
  ]
}

const getConfigForm = () => {
  return {
    source: '1',
    enable: 0,
    planName: '',
    tdate: '',
    activeNum: '',
    newUserNum: '',
    createRoleNum: '',
    sumMoney: ''
  }
}

const configForm = ref(getConfigForm())

const tableData = ref([])
const modalVisible = computed({
  get() {
    return props.value
  },
  set(val) {
    emit('input', val)
  }
})

const getData = async () => {
  const data = await queryProxyImportDataByPlanId({ planId: props.planInfo.planId })
  if (data?.length) {
    tableData.value = data.map((row) => {
      return {
        ...row,
        __highlight: false,
        tdate: moment(row.tdate).format('YYYY-MM-DD')
      }
    })
    const [firstRow] = data
    configForm.value = {
      ...getConfigForm(),
      enable: firstRow.enable
    }
  } else {
    configForm.value = getConfigForm()
    tableData.value = []
  }
}

const onAddConfigTableRow = () => {
  formRef.value.validate((valid) => {
    if (valid) {
      const dateIndex = tableData.value.findIndex((item) => item.tdate === configForm.value.tdate)
      const newRow = { ...configForm.value, __highlight: true }

      if (dateIndex > -1) {
        tableData.value.splice(dateIndex, 1, newRow)
      } else {
        tableData.value.unshift(newRow)
      }

      const targetIndex = dateIndex > -1 ? dateIndex : tableData.value.length - 1
      setTimeout(() => {
        const target = tableData.value[targetIndex]
        if (target) target.__highlight = false
      }, 500)
    }
  })
}
const onTableRowRemove = (index) => {
  tableData.value = tableData.value.filter((_, i) => i !== index)
}

const onModalOpen = () => {
  getData()
}
const onConfigConfirm = () => {
  const list = tableData.value.map((item) => {
    const { __highlight, planName, source, enable, ...rest } = item
    return rest
  })
  submitLoading.value = true
  insertProxyImportDataByPlanId({
    planId: props.planInfo.planId,
    enable: configForm.value.enable,
    list
  })
    .then((data) => {
      if (data) {
        Message.success('录入成功')
        configForm.value = getConfigForm()
        onModalClose()
      }
    })
    .finally(() => {
      submitLoading.value = false
    })
}
const onModalClose = () => {
  modalVisible.value = false
  nextTick(() => {
    formRef.value.clearValidate()
  })
}

// 为新增/更新的表格行添加高亮渐变消失效果
const rowClassName = ({ row }) => {
  return row.__highlight ? 'row-highlight-animate' : ''
}
</script>

<style scoped lang="scss">
.proxy-control-form {
  overflow: auto;
  padding-right: 10px;
}

/* 表格行高亮淡出动画 */
::v-deep .el-table__row.row-highlight-animate {
  animation: rowHighlightFade 0.5s ease-in-out forwards;
}

@keyframes rowHighlightFade {
  0% {
    background-color: #fffbe6; /* 暖黄色高亮 */
  }
  100% {
    background-color: #fff; /* 回归表格默认背景 */
  }
}
</style>
